import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { horizontalCap, boxSolid, subFrame } from '../solids';
import { rectWall, type MasonryStyles } from './masonry';
import type { PlanRect } from './genericBlock';
import { rectRing } from './cloisterPlan';

/** Domestic ranges behind the open walks. Bay spacing and joinery are reconstruction estimates. */
export function buildCloisterRoom(
  builder: MeshBuilder,
  wall: VertexStyle,
  styles: MasonryStyles,
  frame: Frame,
  rect: PlanRect,
  eaveY: number
): void {
  const ring = rectRing(rect);
  const trim = { ...wall, color: styles.glazing?.reveal.color ?? wall.color };
  const masonry = { ...styles, wall, glazing: { reveal: trim } };
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % ring.length]!;
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    // Very short ends stay solid; longer faces have individual recessed lights below the eaves.
    const bays = length < 2 ? 0 : Math.max(1, Math.floor(length / 3.4));
    const holes = Array.from({ length: bays }, (_, i) => {
      const x = ((i + 0.5) * length) / bays;
      return [
        [x - 0.38, 1.1],
        [x + 0.38, 1.1],
        [x + 0.38, 2.4],
        [x - 0.38, 2.4],
      ] as const;
    });
    rectWall(builder, masonry, frame, [from, to], [0, eaveY], holes, 0);
    const face = subFrame(frame, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
    for (let i = 0; i < bays; i++) {
      const x = ((i + 0.5) * length) / bays;
      boxSolid(builder, trim, face, [x - 0.43, 1.02, -0.09], [x + 0.43, 1.12, 0.03], {
        top: true,
        bottom: true,
      });
      boxSolid(builder, trim, face, [x - 0.035, 1.1, -0.025], [x + 0.035, 2.4, 0.025], {
        top: true,
        bottom: true,
      });
      boxSolid(builder, trim, face, [x - 0.38, 1.78, -0.025], [x + 0.38, 1.85, 0.025], {
        top: true,
        bottom: true,
      });
    }
  });
  horizontalCap(builder, wall, frame, ring, 0, false);
  horizontalCap(builder, wall, frame, ring, eaveY, true);
}
